import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, TextField } from '@mui/material';

import { ContactApiError, submitContactForm } from 'services/contactApi';

const FIELDS = [
  { name: 'firstName', label: 'First Name', required: true, half: true },
  { name: 'lastName', label: 'Last Name', required: true, half: true },
  { name: 'company', label: 'Company' },
  { name: 'email', label: 'Email', required: true, type: 'email' },
  { name: 'phone', label: 'Phone Number', type: 'tel' },
  { name: 'message', label: 'Message', required: true, multiline: true, rows: 4 },
];

const EMPTY = FIELDS.reduce((values, field) => ({ ...values, [field.name]: '' }), {});

/**
 * The contact form.
 *
 * Three things here are not decoration:
 *  - `website` is a honeypot: rendered, hidden from people, and left empty by
 *    them. The server treats a filled one as a bot.
 *  - `renderedAt` stamps when the form appeared, so the server can reject a
 *    submission that arrived impossibly fast or was replayed a day later.
 *  - Client-side `required` is UX only. The server validates independently and
 *    its field-level rejection is what marks an input in error.
 */
const ContactForm = ({ submit = submitContactForm }) => {
  const [values, setValues] = React.useState(EMPTY);
  const [status, setStatus] = React.useState({ state: 'idle', message: '' });
  const [fieldError, setFieldError] = React.useState(null);
  const renderedAt = React.useRef(Date.now());

  const handleChange = (name) => (event) => {
    const { value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
    setFieldError((previous) => (previous === name ? null : previous));
  };

  const handleReset = () => {
    setValues(EMPTY);
    setStatus({ state: 'idle', message: '' });
    setFieldError(null);
    renderedAt.current = Date.now();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status.state === 'sending') return;

    setStatus({ state: 'sending', message: '' });
    setFieldError(null);
    try {
      const message = await submit({ ...values, renderedAt: renderedAt.current });
      setStatus({ state: 'sent', message });
      setValues(EMPTY);
      renderedAt.current = Date.now();
    } catch (error) {
      setStatus({
        state: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      });
      if (error instanceof ContactApiError && error.field) setFieldError(error.field);
    }
  };

  const sending = status.state === 'sending';

  if (status.state === 'sent') {
    return (
      <Alert severity="success" sx={{ mt: 2 }} data-testid="contact-success">
        {status.message}
      </Alert>
    );
  }

  return (
    <form id="contact-form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={3}>
        {FIELDS.map((field) => (
          <Grid size={{ xs: 12, sm: field.half ? 6 : 12 }} key={field.name}>
            <TextField
              fullWidth
              required={field.required}
              id={field.name}
              name={field.name}
              label={field.label}
              type={field.type || 'text'}
              value={values[field.name]}
              onChange={handleChange(field.name)}
              disabled={sending}
              error={fieldError === field.name}
              multiline={Boolean(field.multiline)}
              maxRows={field.rows}
              margin="normal"
              variant="standard"
            />
          </Grid>
        ))}
      </Grid>

      {/* Honeypot: off-screen rather than display:none, which some bots skip. */}
      <Box
        aria-hidden="true"
        sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor="website">Leave this field empty</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website || ''}
          onChange={handleChange('website')}
        />
      </Box>

      {status.state === 'error' && (
        <Alert severity="error" sx={{ mt: 3 }} data-testid="contact-error">
          {status.message}
        </Alert>
      )}

      <Grid container direction="row" spacing={3} sx={{ mt: 0 }}>
        <Grid>
          <Button type="button" onClick={handleReset} disabled={sending}>
            RESET
          </Button>
        </Grid>
        <Grid>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {sending ? 'SENDING…' : 'SUBMIT'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ContactForm;
